import React, { useEffect, useState } from "react";
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, ToggleButton, ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import { zkApi } from "./zkApi";
import { apiError } from "./zkStatusStyles";
import { displayName, isDeviceAdmin, presetDates, shortDate } from "./doorAccessText";

// "Let in" / "Block" one person until a date (an override). Shared by the simple view, Advanced and the
// member profile. target = a door access row (+ action "allow" | "block"); sn = device serial number.
export default function DoorAccessDialog({ target, sn, today, onClose, onDone }) {
  const [preset, setPreset] = useState("today");
  const [until, setUntil] = useState(today || "");
  const [note, setNote] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target) {
      setPreset("today");
      setUntil(today || "");
      setNote("");
      setConfirmPin("");
    }
  }, [target, today]);

  if (!target) return null;
  const block = target.action === "block";
  const byPin = !!target.manual;                      // staff / device admin: stored by PIN
  const needsConfirm = byPin && block && isDeviceAdmin(target);
  const presets = presetDates(today);

  const choose = (key) => {
    if (!key) return;
    setPreset(key);
    const p = presets.find((x) => x.key === key);
    if (p) setUntil(p.date);
  };

  const save = async () => {
    setSaving(true);
    try {
      const who = byPin
        ? { serial_number: sn, pin: target.pin, confirm_pin: needsConfirm ? confirmPin : undefined }
        : { membership_id: target.membership_id };
      await zkApi("post", "/hpc/admin/zk/access/overrides", {
        data: { ...who, action: target.action, valid_until: until, note: note || undefined },
      });
      toast.success(`${displayName(target)}: ${block ? "blocked" : "let in"} until ${shortDate(until)}`);
      onDone();
    } catch (e) {
      toast.error(apiError(e, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{block ? "Block" : "Let in"} {displayName(target)}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {block ? "Stop this person opening the gym door until:" : "Let this person open the gym door until:"}
        </Typography>
        <ToggleButtonGroup size="small" exclusive value={preset} onChange={(e, v) => choose(v)}
          sx={{ mb: 2, flexWrap: "wrap" }}>
          {presets.map((p) => <ToggleButton key={p.key} value={p.key}>{p.label}</ToggleButton>)}
          <ToggleButton value="pick">Pick a date</ToggleButton>
        </ToggleButtonGroup>
        {preset === "pick" && (
          <TextField fullWidth size="small" type="date" label="Until (this day included)" InputLabelProps={{ shrink: true }}
            value={until} inputProps={{ min: today }} onChange={(e) => setUntil(e.target.value)} sx={{ mb: 2 }} />
        )}
        <TextField fullWidth size="small" label="Note (optional)" placeholder={block ? "e.g. card lost" : "e.g. paid cash, receipt tomorrow"}
          value={note} inputProps={{ maxLength: 255 }} onChange={(e) => setNote(e.target.value)} />
        {needsConfirm && (
          <>
            <Alert severity="warning" sx={{ mt: 2, mb: 1 }}>
              This person is a device admin. Type their PIN ({target.pin}) to confirm.
            </Alert>
            <TextField fullWidth size="small" label="PIN" value={confirmPin} inputProps={{ inputMode: "numeric" }}
              onChange={(e) => setConfirmPin(e.target.value.trim())} />
          </>
        )}
        <Typography variant="caption" sx={{ display: "block", mt: 1.5, opacity: 0.75 }}>
          {until ? `Until the end of ${shortDate(until)}. ` : ""}After that the normal rules apply again.
          You can undo it any time with "Back to normal".
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color={block ? "error" : "success"}
          disabled={saving || !until || (needsConfirm && confirmPin !== target.pin)} onClick={save}>
          {block ? "Block" : "Let in"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// "Back to normal": remove the person's exception.
export async function backToNormal(row) {
  try {
    await zkApi("delete", `/hpc/admin/zk/access/overrides/${row.override.id}`);
    toast.success(`${displayName(row)}: back to normal rules`);
    return true;
  } catch (e) {
    toast.error(apiError(e, "Could not remove the exception"));
    return false;
  }
}
